import { IconButton } from '@monorepo/shared/componentsV2/Button/IconButton';
import { StyledFormGroup } from '@monorepo/shared/componentsV2/styled/form';
import { VisuallyHidden } from '@monorepo/shared/componentsV2/VisuallyHidden';
import TrashIcon from '@svg/trash.svg';
import { DatasetResponse } from 'mapistry-shared';
import React, { useCallback, useMemo } from 'react';
import { useField } from 'react-final-form';
import styled from 'styled-components';
import { LeftConditionPicker, RightConditionPicker } from './ConditionPicker';
import { filterConditionProperties } from './helpers';

const StyledEqualsText = styled.div<{
  $shouldLabelShow: boolean;
}>`
  margin-top: ${({ $shouldLabelShow }) =>
    $shouldLabelShow
      ? '1.7rem'
      : '0.6rem'}; /* lines this up vertically with the input elements to its left */
`;

const StyledConditionDeleteButton = styled(IconButton)<{
  $shouldLabelShow: boolean;
}>`
  min-width: 2rem;
  margin-top: ${({ $shouldLabelShow }) =>
    $shouldLabelShow
      ? '1.2rem'
      : '0'}; /* lines this up vertically with the input elements to its left */

  font-size: 0.75rem;
`;

interface StepConditionProps {
  formFieldName: string;
  leftDataset: DatasetResponse;
  rightDataset?: DatasetResponse;
  shouldLabelShow: boolean;
  canDelete: boolean;
  onDelete: () => void;
  setCondition: (
    leftPropertyId: string,
    rightPropertyId: string | undefined,
  ) => void;
}

export function StepCondition({
  formFieldName,
  leftDataset,
  rightDataset,
  shouldLabelShow,
  canDelete,
  onDelete,
  setCondition,
}: StepConditionProps) {
  const leftFieldName = `${formFieldName}.leftPropertyId`;
  const rightFieldName = `${formFieldName}.rightPropertyId`;
  const {
    input: { value: selectedLeftPropertyId },
  } = useField<string>(leftFieldName);
  const {
    input: { value: selectedRightPropertyId },
  } = useField<string>(rightFieldName);
  const selectedLeftProperty = useMemo(
    () =>
      selectedLeftPropertyId
        ? leftDataset?.schemaProperties?.find(
            (property) => property.id === selectedLeftPropertyId,
          )
        : undefined,
    [leftDataset?.schemaProperties, selectedLeftPropertyId],
  );

  const tryChangeRightPropertyWhenLeftPropertyChanges = useCallback(
    (leftPropertyId: string) => {
      if (!leftDataset || !rightDataset) {
        return;
      }
      const leftProperty = leftDataset.schemaProperties.find(
        (p) => p.id === leftPropertyId,
      );
      const validRightPropertyIds = rightDataset.schemaProperties
        .filter((p) =>
          // This is the same filtering that is used for the RightConditionPicker
          // However, explicitly set selectedPropertyId: undefined here so that
          // we don't allow the currently selected right property if it's not valid.
          filterConditionProperties(p, {
            leftProperty,
            selectedPropertyId: undefined,
          }),
        )
        .map((p) => p.id);

      // When changing left property, treat right as the dependent property.
      // So we can set it if there's only one match, or clear it if there are none.
      if (validRightPropertyIds.length === 1) {
        setCondition(leftPropertyId, validRightPropertyIds[0]);
      } else if (!validRightPropertyIds.includes(selectedRightPropertyId)) {
        setCondition(leftPropertyId, undefined);
      }
    },
    [leftDataset, rightDataset, setCondition, selectedRightPropertyId],
  );

  return (
    <StyledFormGroup row>
      <LeftConditionPicker
        shouldLabelShow={shouldLabelShow}
        label="Field 1"
        dataset={leftDataset}
        fieldName={leftFieldName}
        onChange={tryChangeRightPropertyWhenLeftPropertyChanges}
      />

      <StyledEqualsText $shouldLabelShow={shouldLabelShow}>
        equals
      </StyledEqualsText>

      <RightConditionPicker
        shouldLabelShow={shouldLabelShow}
        label="Field 2"
        dataset={rightDataset}
        fieldName={rightFieldName}
        selectedLeftProperty={selectedLeftProperty}
      />

      {canDelete && (
        <StyledConditionDeleteButton
          onClick={onDelete}
          $shouldLabelShow={shouldLabelShow}
        >
          <TrashIcon />
          <VisuallyHidden>Delete condition</VisuallyHidden>
        </StyledConditionDeleteButton>
      )}
    </StyledFormGroup>
  );
}
